import { SelectChangeEvent } from '@mui/material/Select'
import React, { useEffect, useRef, useState } from 'react'

import { createComponentWrapperWithRef, styled, uiTokens } from '../../theme'
import { COLORS } from '../../theme/colors'
import { findDuplicates, OptionItem } from '../../utils'
import { ButtonIcon } from '../Button'
import { Checkbox } from '../Checkbox'
import { IconSelector } from '../Icons'
import { List } from '../List'
import { MenuItem } from '../Menu'
import { BaseSelect, SelectInputAdornment, selectValueRenderer } from './BaseSelect'
import { OptionItemComponent } from './OptionItem'

const ALL_VALUE = crypto.randomUUID()

// TODO: Why is this being used instead of the Select component?
/**
 * @deprecated This component will be removed in a future release.
 * Please use `<BaseSelect />` instead.
 */
export const StyledSelect = BaseSelect

const ButtonsWrapper = styled(SelectInputAdornment)`
  padding-right: 7px;
`

/* StyledMenuItem */
const MuiStyledMenuItem = styled(MenuItem, { shouldForwardProp: (prop) => prop !== '$color' && prop !== '$level' })<{
  $color?: string
  $level?: number
}>`
  padding: 12px 16px;

  ${(props) => (props.$color ? `color: ${props.$color};` : '')}
  ${(props) => (props.$level ? `padding-left: ${props.$level * 40 + 16}px;` : '')}

  gap: 9px;
  &:hover {
    background-color: ${COLORS.action.hover};
  }
`
const NovaStyledMenuItem = styled(MenuItem, { shouldForwardProp: (prop) => prop !== '$color' && prop !== '$level' })<{
  $color?: string
  $level?: number
}>`
  padding: 8px 16px;

  ${(props) => (props.$color ? `color: ${props.$color};` : '')}
  ${(props) => (props.$level ? `padding-left: ${props.$level * 40 + 16}px;` : '')}

  gap: 9px;
  &:hover {
    color: ${uiTokens.uiContent.emphasized};
  }
`
const StyledMenuItem = createComponentWrapperWithRef(
  {
    mui: MuiStyledMenuItem,
    nova: NovaStyledMenuItem,
  },
  'StyledMenuItem',
)

const StyledCheckbox = styled(Checkbox)`
  margin: -9px;
`

const StyledList = styled(List)`
  padding: 0;
`

const SelectButton = styled(ButtonIcon)`
  padding: 2px;
  & > svg {
    height: 24px;
    width: 24px;
  }
`

const ClearButton = styled(ButtonIcon)`
  margin-right: -2px;
  padding: 4px;
  & > svg {
    height: 20px;
    width: 20px;
  }
`

export function Select(props: {
  label: string
  multi?: boolean
  options: OptionItem[]
  value: OptionItem | OptionItem[] | null | undefined
  onChange: (value: string | string[]) => void
  disabled?: boolean
  allLabel?: string
  ignoredItemsID?: string[]
  onClear?: () => void
  onOpen?: () => void
  onClose?: () => void
  canBeUnselected?: boolean
}) {
  const [hierarchy, setHierarchy] = useState(new Map<string, OptionItem[]>())
  const [open, setOpen] = useState(false)
  const [hover, setHover] = useState(false)
  const openRef = useRef(false)
  useEffect(() => {
    const newHierarchy = buildHierarchy()
    setHierarchy(newHierarchy)
  }, [props.options])

  function openMenu() {
    if (props.disabled || openRef.current) {
      return
    }

    openRef.current = true
    props.onOpen?.()
    setOpen(true)
  }

  function closeMenu() {
    if (!openRef.current) {
      return
    }

    openRef.current = false
    setOpen(false)
    props.onClose?.()
  }

  function toggleMenu() {
    if (openRef.current) {
      closeMenu()
      return
    }

    openMenu()
  }

  function handleChange(event: SelectChangeEvent<unknown>) {
    const { value } = event.target
    let toSend: string[] =
      typeof value === 'string' ? value.split(',') : (value as any[]).map((v) => (v?.value ? v.value : v))

    if (toSend.includes(ALL_VALUE)) {
      return
    }
    /*
     * the MUI component returns a list of values (where the already selected one are an instance of `OptionItem`
     * and the new one is a plain string) in wich the deselected value by the user
     * is added as a duplicate item at the bottom of the array
     * to handle this corner case i check for duplicate items
     * and then remove them as they signify a deselected value...
     */
    const duplicates = findDuplicates(toSend)
    toSend = toSend.filter((i) => !duplicates.includes(i))
    props.onChange(toSend)
  }

  function checkValue(e: OptionItem): boolean {
    const r = ((props.value as any) ?? []).findIndex((el: OptionItem) => el.value === e.value)
    return r !== -1
  }

  function buildHierarchy() {
    const groupMap = new Map<string, OptionItem[]>()

    props.options.forEach((opt) => {
      if (opt.parentId) {
        let parentNode = groupMap.get(opt.parentId)
        if (!parentNode) {
          groupMap.set(opt.parentId, [])
          parentNode = groupMap.get(opt.parentId)
        }

        parentNode?.push(opt)
      } else {
        let parentNode = groupMap.get('ROOT')
        if (!parentNode) {
          groupMap.set('ROOT', [])
          parentNode = groupMap.get('ROOT')
        }

        parentNode?.push(opt)
      }
    })

    return groupMap
  }

  function isGroupChecked(groupKey: string) {
    let result = false
    if (groupKey !== 'ROOT') {
      const groupOpts = hierarchy.get(groupKey)
      result = !!groupOpts?.some((opt) => !!((props.value as OptionItem[]) ?? []).find((o) => opt.value === o.value))
    } else {
      const hierarchyResults: { some: boolean }[] = []
      hierarchy.forEach((opts) => {
        const some = !!opts?.some((opt) => !!((props.value as OptionItem[]) ?? []).find((o) => opt.value === o.value))
        hierarchyResults.push({ some })
      })
      result = hierarchyResults.some((r) => r.some)
    }
    return result
  }

  function isGroupIntermediate(groupKey: string) {
    let result = false
    // A normal group to be intermediate has to have some of the options selected but not all
    if (groupKey !== 'ROOT') {
      const groupOpts = hierarchy.get(groupKey)
      const some = !!groupOpts?.some(
        (opt) => !!((props.value as OptionItem[]) ?? []).find((o) => opt.value === o.value),
      )
      const all = !!groupOpts?.every(
        (opt) => !!((props.value as OptionItem[]) ?? []).find((o) => opt.value === o.value || isOptionIgnored(opt)),
      )
      result = some && !all
      // The ROOT group to be intermediate has to have at least some group that has some option selected,
      // but has to make sure that not all the group are completely selected
    } else {
      const hierarchyResults: { some: boolean; all: boolean }[] = []
      hierarchy.forEach((opts) => {
        const some = !!opts?.some((opt) => !!((props.value as OptionItem[]) ?? []).find((o) => opt.value === o.value))
        const all = !!opts?.every(
          (opt) => !!((props.value as OptionItem[]) ?? []).find((o) => opt.value === o.value || isOptionIgnored(opt)),
        )
        hierarchyResults.push({ some, all })
      })
      result = hierarchyResults.some((r) => r.some || r.all) && !hierarchyResults.every((r) => r.all)
    }
    return result
  }

  function isGroupDisabled(groupKey: string): boolean {
    let result = false
    if (groupKey !== 'ROOT') {
      const groupOpts = hierarchy.get(groupKey)
      result = !!groupOpts?.every((opt) => opt.disabled)
    } else {
      const hierarchyResults: { all: boolean }[] = []
      hierarchy.forEach((opts) => {
        const all = !!opts?.every((opt) => !!((props.value as OptionItem[]) ?? []).find((o) => opt.disabled))
        hierarchyResults.push({ all })
      })
      result = hierarchyResults.every((r) => r.all)
    }
    return result
  }

  function isOptionIgnored(optionToCheck: OptionItem) {
    return props.ignoredItemsID?.some((x) => x == optionToCheck?.value)
  }

  function areEnabledOptionsSelected(groupKey: string) {
    let result = false
    if (groupKey !== 'ROOT') {
      const groupOpts = hierarchy.get(groupKey)
      result = !!groupOpts?.every(
        (opt) =>
          !!((props.value as OptionItem[]) ?? []).find(
            (o) => opt.value === o.value || opt.disabled || isOptionIgnored(opt),
          ),
      )
    } else {
      const hierarchyResults: { all: boolean }[] = []
      hierarchy.forEach((opts) => {
        const all = !!opts?.every(
          (opt) =>
            !!((props.value as OptionItem[]) ?? []).find(
              (o) => opt.value === o.value || opt.disabled || isOptionIgnored(opt),
            ),
        )
        hierarchyResults.push({ all })
      })
      result = hierarchyResults.every((r) => r.all)
    }
    return result
  }

  function onGroupClick(groupKey: string, e: React.MouseEvent<any>) {
    e.stopPropagation()
    // If the group is not the root one toggle all the children
    if (groupKey !== 'ROOT') {
      const groupOpts = hierarchy.get(groupKey)
      let current = Array.isArray(props.value) ? [...props.value] : [props.value]
      current = current.concat(groupOpts)
      let values = current.filter((o) => o && !o.disabled && !isOptionIgnored(o)).map((o) => o?.value) as string[]
      const duplicates = findDuplicates(values)
      // make sure values are present only 1 time
      values = values.filter((i) => !duplicates.includes(i))
      values = values.concat(duplicates)
      // If the group is all checked the click acts like a toggle
      // and deselects all the items in the group
      if (isGroupChecked(groupKey) && (!isGroupIntermediate(groupKey) || areEnabledOptionsSelected(groupKey))) {
        values = values.filter((v) => !groupOpts?.find((o) => o.value === v && !o.disabled))
      }
      handleChange({ target: { value: values.length ? values.join(',') : [] } } as any)
      // If the group is the ROOT traverse all the hierarchy and toggle al groups and their children
    } else {
      let keys: OptionItem[] = []
      hierarchy.forEach((opts) => {
        keys = keys.concat(opts)
      })
      let current = Array.isArray(props.value) ? [...props.value] : [props.value]
      current = current.concat(keys)
      let values = current.filter((o) => o && !o.disabled && !isOptionIgnored(o)).map((o) => o?.value) as string[]
      const duplicates = findDuplicates(values)
      // make sure values are present only 1 time
      values = values.filter((i) => !duplicates.includes(i))
      values = values.concat(duplicates)
      // If the group is all checked the click acts like a toggle
      // and deselects all the items in the group
      if (isGroupChecked('ROOT') && (!isGroupIntermediate('ROOT') || areEnabledOptionsSelected('ROOT'))) {
        values = values.filter((v) => !keys.find((o) => o.value === v && !o.disabled))
      }
      handleChange({ target: { value: values.length ? values.join(',') : [] } } as any)
    }
  }

  function onHierarchyClick(value: OptionItem, e: React.MouseEvent<any>) {
    e.stopPropagation()
    const current = Array.isArray(props.value) ? [...props.value] : [props.value]
    current.push(value)
    handleChange({ target: { value: current.map((o) => o?.value).join(',') } } as any)
  }

  const hasValue = props.value && (Array.isArray(props.value) ? props.value.length > 0 : true)
  const canBeUnselected = props.canBeUnselected ?? true
  const shouldClear = canBeUnselected && hasValue && hover && !props.disabled && !!props.onClear

  function buildSingleGroup(opts: OptionItem[], groupKey: string, root?: boolean, multi?: boolean, level?: number) {
    return (
      <StyledList key={`group-${groupKey}`}>
        {!root && (
          <StyledMenuItem
            onClick={(e: React.MouseEvent) => onGroupClick(groupKey, e)}
            $level={level}
            disabled={isGroupDisabled(groupKey)}
          >
            {multi && (
              <StyledCheckbox
                tabIndex={-1}
                disableRipple
                checked={isGroupChecked(groupKey)}
                indeterminate={isGroupIntermediate(groupKey)}
                disabled={isGroupDisabled(groupKey)}
              />
            )}
            <OptionItemComponent label={opts[0].parentLabel ?? ''} icon={opts[0].icon} color={opts[0].color} />
          </StyledMenuItem>
        )}
        <StyledList>
          {opts.map((opt) => (
            <StyledMenuItem
              $level={level ? level + 1 : 1}
              data-test-id="menu-item"
              onClick={(e) => onHierarchyClick(opt, e)}
              key={opt.value}
              value={opt.value}
              disabled={opt.disabled}
            >
              <StyledCheckbox
                data-test-id="menu-item-checkbox"
                checked={checkValue(opt)}
                disableRipple
                disabled={opt.disabled}
              />
              <OptionItemComponent label={opt.label} icon={opt.icon} color={opt.color} rightLabel={opt.rightLabel} />
            </StyledMenuItem>
          ))}
        </StyledList>
      </StyledList>
    )
  }

  function getGroupSelect(multi?: boolean) {
    const results: React.ReactNode[] = []
    hierarchy.forEach((opts, key) => {
      if (key !== 'ROOT') results.push(buildSingleGroup(opts, key, false, true, 1))
    })

    return (
      <>
        {multi && props.allLabel && (
          <StyledMenuItem onClick={(e: React.MouseEvent) => onGroupClick('ROOT', e)} disabled={isGroupDisabled('ROOT')}>
            <StyledCheckbox
              tabIndex={-1}
              disableRipple
              checked={isGroupChecked('ROOT')}
              indeterminate={isGroupIntermediate('ROOT')}
              disabled={isGroupDisabled('ROOT')}
            />
            <OptionItemComponent label={props.allLabel} />
          </StyledMenuItem>
        )}
        {buildSingleGroup(hierarchy.get('ROOT') as OptionItem[], 'ROOT', true, multi, 0)}
        {results}
      </>
    )
  }

  return (
    <BaseSelect
      open={open}
      onClose={closeMenu}
      onOpen={openMenu}
      data-test-id="select"
      label={props.label}
      value={props.value}
      multiple={props.multi}
      onChange={handleChange}
      renderValue={(value) => selectValueRenderer(value as OptionItem | OptionItem[] | null, props.options)}
      disabled={props.disabled}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      IconComponent={() => null}
      MenuProps={{
        slotProps: {
          paper: {
            sx: {
              maxHeight: '60dvh',
            },
          },
        },
      }}
      endAdornment={
        <ButtonsWrapper onClick={toggleMenu}>
          {shouldClear && (
            <ClearButton
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation()
                props.onClear && props.onClear()
              }}
            >
              <IconSelector iconName="Close" />
            </ClearButton>
          )}
          <SelectButton disabled={props.disabled}>
            <IconSelector iconName={open ? 'ArrowDropUp' : 'ArrowDropDown'} />
          </SelectButton>
        </ButtonsWrapper>
      }
    >
      {hierarchy.size > 1 && props.multi && getGroupSelect(true)}
      {hierarchy.size > 1 && !props.multi && getGroupSelect(false)}

      {hierarchy.size == 1 && props.multi && props.allLabel && (
        <StyledMenuItem
          data-test-id="menu-item-all"
          value={ALL_VALUE}
          onClick={(e) => onGroupClick('ROOT', e)}
          $level={0}
        >
          <StyledCheckbox
            tabIndex={-1}
            disableRipple
            checked={isGroupChecked('ROOT')}
            indeterminate={isGroupIntermediate('ROOT')}
            disabled={isGroupDisabled('ROOT')}
          />
          <OptionItemComponent data-test-id="list-item-text1" label={props.allLabel} />
        </StyledMenuItem>
      )}

      {hierarchy.size == 1 &&
        props.multi &&
        props.options.map((opt) => (
          <StyledMenuItem data-test-id="menu-item" key={opt.value} value={opt.value} disabled={opt.disabled} $level={0}>
            <StyledCheckbox
              data-test-id="menu-item-checkbox"
              checked={checkValue(opt)}
              disableRipple
              disabled={opt.disabled}
            />
            <OptionItemComponent
              data-test-id="list-item-text"
              label={opt.label}
              icon={opt.icon}
              color={opt.color}
              rightLabel={opt.rightLabel}
            />
          </StyledMenuItem>
        ))}
      {hierarchy.size == 1 &&
        !props.multi &&
        props.options.map((opt) => (
          <StyledMenuItem data-test-id="menu-item" key={opt.value} value={opt.value} $color={opt.color} $level={0}>
            <OptionItemComponent
              data-test-id="list-item-text"
              label={opt.label}
              icon={opt.icon}
              color={opt.color}
              rightLabel={opt.rightLabel}
            />
          </StyledMenuItem>
        ))}
    </BaseSelect>
  )
}
